import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Select, Button, Tag, Typography, Spin, notification,
    Input, Row, Col, Statistic, Progress, Space, Tabs, Tooltip, Badge,
} from 'antd';
import {
    CheckCircleOutlined, ReloadOutlined, SearchOutlined, ClearOutlined,
    ThunderboltOutlined, EditOutlined, WarningOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import _ from 'lodash';
import { baseUrl } from '../../config';

const { Text, Title } = Typography;
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
const norm = s => (s || '').normalize('NFC').trim().toLowerCase();

// Reusable village search select
function VillageSelect({ rowKey, onSelect, value, options, onSearch, loading }) {
    return (
        <Select
            showSearch
            placeholder="Search & select correct village..."
            filterOption={false}
            onSearch={q => onSearch(q, rowKey)}
            loading={loading}
            notFoundContent={loading ? <Spin size="small" /> : 'Type to search'}
            allowClear
            style={{ width: '100%', minWidth: 280 }}
            options={options || []}
            onChange={onSelect}
            value={value}
        />
    );
}

export default function BulkCorrection() {
    const [mode, setMode]               = useState('unlinked'); // 'unlinked' | 'linked'

    // Unlinked state
    const [unlinked, setUnlinked]       = useState([]);
    const [unlinkedLoading, setUnlinkedLoading] = useState(false);
    const [done, setDone]               = useState(0);

    // Linked (re-correct) state
    const [linked, setLinked]           = useState([]);
    const [linkedLoading, setLinkedLoading] = useState(false);
    const [linkedDone, setLinkedDone]   = useState(0);

    // Shared
    const [applying, setApplying]       = useState({});
    const [bulkApplying, setBulkApplying] = useState(false);
    const [selections, setSelections]   = useState({});
    const [villageOptions, setVillageOptions] = useState({});
    const [villageSearching, setVillageSearching] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize]       = useState(50);
    const [filterVillage, setFilterVillage]   = useState('');
    const [filterTehsil, setFilterTehsil]     = useState('');
    const [filterDistrict, setFilterDistrict] = useState('');

    const fetchUnlinked = async () => {
        setUnlinkedLoading(true);
        try {
            const res = await axios.get(`${baseUrl}/village/unlinked-list`, AUTH());
            setUnlinked(res.data.result || []);
            setDone(0); setSelections({}); setSelectedKeys([]); setCurrentPage(1);
        } catch { notification.error({ message: 'Failed to load unlinked villages' }); }
        finally { setUnlinkedLoading(false); }
    };

    const fetchLinked = async () => {
        setLinkedLoading(true);
        try {
            const res = await axios.get(`${baseUrl}/village/linked-list`, AUTH());
            setLinked(res.data.result || []);
            setLinkedDone(0); setSelections({}); setSelectedKeys([]); setCurrentPage(1);
        } catch { notification.error({ message: 'Failed to load linked villages' }); }
        finally { setLinkedLoading(false); }
    };

    useEffect(() => { fetchUnlinked(); }, []);

    const handleTabChange = (key) => {
        setMode(key);
        setSelections({}); setSelectedKeys([]); setCurrentPage(1);
        setFilterVillage(''); setFilterTehsil(''); setFilterDistrict('');
        if (key === 'linked' && linked.length === 0) fetchLinked();
    };

    const handleReset = () => {
        setFilterVillage(''); setFilterTehsil(''); setFilterDistrict('');
        setCurrentPage(1); setSelectedKeys([]);
    };

    const searchVillage = useCallback(_.debounce(async (q, rowKey) => {
        if (!q || q.length < 1) { setVillageOptions(prev => ({ ...prev, [rowKey]: [] })); return; }
        setVillageSearching(true);
        try {
            const res = await axios.get(`${baseUrl}/village/search`, { ...AUTH(), params: { q } });
            const opts = (res.data.result || []).map(v => {
                const loc = [v.tehsil_en, v.district_en].filter(Boolean).join(', ');
                return { value: v.id, label: loc ? `${v.name_en} — ${loc}` : v.name_en };
            });
            setVillageOptions(prev => ({ ...prev, [rowKey]: opts }));
        } catch { setVillageOptions(prev => ({ ...prev, [rowKey]: [] })); }
        finally { setVillageSearching(false); }
    }, 300), []);

    const setSelection = (rowKey, val, opt) => {
        if (val) setSelections(prev => ({ ...prev, [rowKey]: { village_id: val, label: opt?.label } }));
        else setSelections(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
    };

    const handleApply = async (record, force = false) => {
        const rowKey = record.village_en;
        const sel = selections[rowKey];
        if (!sel) { notification.warning({ message: 'Please select a master village first' }); return false; }
        setApplying(prev => ({ ...prev, [rowKey]: true }));
        try {
            const res = await axios.post(`${baseUrl}/village/bulk-correct`,
                { village_en: rowKey, village_id: sel.village_id, force }, AUTH());
            notification.success({ message: `${res.data.updated} members updated` });
            if (force) {
                setLinkedDone(d => d + 1);
                setLinked(prev => prev.filter(r => r.village_en !== rowKey));
            } else {
                setDone(d => d + 1);
                setUnlinked(prev => prev.filter(r => r.village_en !== rowKey));
            }
            return true;
        } catch (e) {
            notification.error({ message: e.response?.data?.message || 'Update failed' });
            return false;
        } finally { setApplying(prev => ({ ...prev, [rowKey]: false })); }
    };

    const handleBulkApply = async (force = false) => {
        const data = force ? linked : unlinked;
        const filteredData = applyFilters(data);
        const selectedRows = filteredData.filter(r => selectedKeys.includes(r.village_en));
        const unmapped = selectedRows.filter(r => !selections[r.village_en]);
        if (unmapped.length > 0) {
            notification.warning({
                message: `${unmapped.length} selected row(s) have no master village mapped`,
            });
            return;
        }
        setBulkApplying(true);
        let count = 0;
        for (const record of selectedRows) {
            const ok = await handleApply(record, force);
            if (ok) count++;
        }
        setBulkApplying(false);
        setSelectedKeys([]);
        if (count > 0) notification.success({ message: `Bulk applied: ${count} village(s) corrected` });
    };

    const applyFilters = (data) => data.filter(r => {
        const v = norm(filterVillage), t = norm(filterTehsil), d = norm(filterDistrict);
        return (
            (!v || norm(r.village_en).includes(v) || norm(r.village_hn).includes(v)) &&
            (!t || norm(r.tehsil_en).includes(t)) &&
            (!d || norm(r.district_en).includes(d))
        );
    });

    const filtered = applyFilters(mode === 'unlinked' ? unlinked : linked);
    const isForce = mode === 'linked';
    const selectedMappedCount = selectedKeys.filter(k => selections[k]).length;

    const filterBar = (
        <Card size="small" style={{ marginBottom: 12, background: '#f8f9fa', border: '1px solid #e0e6ee' }}>
            <Row gutter={[12, 8]} align="middle">
                <Col xs={24} sm={8} md={7}>
                    <Input prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                        placeholder="Village / गाँव खोजें..." value={filterVillage}
                        onChange={e => { setFilterVillage(e.target.value); setCurrentPage(1); setSelectedKeys([]); }}
                        allowClear />
                </Col>
                <Col xs={24} sm={6} md={5}>
                    <Input prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                        placeholder="Tehsil / तहसील..." value={filterTehsil}
                        onChange={e => { setFilterTehsil(e.target.value); setCurrentPage(1); }}
                        allowClear />
                </Col>
                <Col xs={24} sm={6} md={5}>
                    <Input prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                        placeholder="District / जिला..." value={filterDistrict}
                        onChange={e => { setFilterDistrict(e.target.value); setCurrentPage(1); }}
                        allowClear />
                </Col>
                <Col xs={24} sm={4} md={7}>
                    <Space wrap>
                        <Button icon={<ClearOutlined />} onClick={handleReset} size="small">Reset</Button>
                        <Tag color={filtered.length < (isForce ? linked : unlinked).length ? 'blue' : 'default'}>
                            {filtered.length} / {(isForce ? linked : unlinked).length}
                        </Tag>
                        {selectedKeys.length > 0 && (
                            <Button type="primary" size="small" icon={<ThunderboltOutlined />}
                                loading={bulkApplying}
                                disabled={selectedMappedCount === 0}
                                onClick={() => handleBulkApply(isForce)}
                                style={{ background: isForce ? '#fa8c16' : '#1677ff' }}
                            >
                                {isForce ? 'Re-apply' : 'Apply'} Selected ({selectedMappedCount}/{selectedKeys.length})
                            </Button>
                        )}
                    </Space>
                </Col>
            </Row>
        </Card>
    );

    const buildColumns = (force) => [
        {
            title: '#', width: 55,
            render: (_, __, i) => <Text type="secondary" style={{ fontSize: 12 }}>{(currentPage - 1) * pageSize + i + 1}</Text>,
        },
        {
            title: 'Current Village (in DB)', key: 'village',
            sorter: (a, b) => norm(a.village_en).localeCompare(norm(b.village_en)),
            defaultSortOrder: 'ascend',
            render: r => (
                <div>
                    <Text strong style={{ color: force ? '#d46b08' : '#cf1322' }}>{r.village_en || '—'}</Text>
                    {r.village_hn && <div style={{ fontSize: 11, color: '#555' }}>{r.village_hn}</div>}
                    <div style={{ fontSize: 11, color: '#888' }}>
                        {[r.tehsil_en && `तहसील: ${r.tehsil_en}`, r.district_en && `जिला: ${r.district_en}`].filter(Boolean).join(' | ')}
                    </div>
                    {force && r.current_master_en && (
                        <div style={{ marginTop: 3 }}>
                            <Tag color={r.master_tehsil_en && r.master_district_en ? 'green' : 'orange'} style={{ fontSize: 10 }}>
                                {r.master_tehsil_en && r.master_district_en ? '✓' : '⚠'} Master: {r.current_master_en}
                                {r.master_tehsil_en ? ` — ${r.master_tehsil_en}` : ''}
                                {r.master_district_en ? `, ${r.master_district_en}` : ''}
                            </Tag>
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Members', dataIndex: 'member_count', key: 'count', width: 90,
            render: v => <Tag color="orange">{v}</Tag>,
            sorter: (a, b) => b.member_count - a.member_count,
        },
        {
            title: force ? 'Map to New Master Village' : 'Map to Master Village', key: 'master',
            render: r => (
                <VillageSelect
                    rowKey={r.village_en}
                    value={selections[r.village_en]?.village_id}
                    options={villageOptions[r.village_en]}
                    onSearch={searchVillage}
                    loading={villageSearching}
                    onSelect={(val, opt) => setSelection(r.village_en, val, opt)}
                />
            ),
        },
        {
            title: 'Action', key: 'action', width: 130,
            render: r => (
                <Button
                    type="primary" size="small"
                    icon={force ? <EditOutlined /> : <CheckCircleOutlined />}
                    loading={applying[r.village_en]}
                    disabled={!selections[r.village_en]}
                    onClick={() => handleApply(r, force)}
                    style={{ background: force ? '#fa8c16' : '#52c41a', borderColor: force ? '#fa8c16' : '#52c41a' }}
                >
                    {force ? 'Re-apply' : 'Apply'}
                </Button>
            ),
        },
    ];

    const tableProps = (data, loading, force) => ({
        rowSelection: { selectedRowKeys: selectedKeys, onChange: keys => setSelectedKeys(keys), columnWidth: 40 },
        dataSource: applyFilters(data),
        rowKey: 'village_en',
        columns: buildColumns(force),
        loading,
        size: 'small',
        pagination: {
            current: currentPage, pageSize,
            total: applyFilters(data).length,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            showTotal: (t, [s, e]) => `${s}–${e} of ${t}`,
            onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
        },
        locale: { emptyText: (force ? linkedDone : done) > 0 ? '✅ All done!' : 'No records found' },
    });

    const totalUnlinked = unlinked.length + done;

    return (
        <div style={{ padding: '16px 20px', background: '#fff', minHeight: '100vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0 }}>Village Bulk Correction — गाँव डेटा सुधार</Title>
                <Button icon={<ReloadOutlined />} onClick={mode === 'unlinked' ? fetchUnlinked : fetchLinked}>Refresh</Button>
            </div>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={8}>
                    <Card size="small" style={{ background: '#fff7e6', border: '1px solid #ffd591' }}>
                        <Statistic title="Unlinked Village Groups" value={unlinked.length} valueStyle={{ color: '#d46b08' }} />
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card size="small" style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
                        <Statistic title="Corrected (this session)" value={done + linkedDone} valueStyle={{ color: '#52c41a' }} />
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card size="small">
                        <div style={{ marginBottom: 4 }}><Text type="secondary" style={{ fontSize: 12 }}>Progress</Text></div>
                        <Progress percent={totalUnlinked > 0 ? Math.round((done / totalUnlinked) * 100) : 0} size="small" status="active" />
                    </Card>
                </Col>
            </Row>

            <Tabs
                activeKey={mode}
                onChange={handleTabChange}
                style={{ marginBottom: 0 }}
                items={[
                    {
                        key: 'unlinked',
                        label: (
                            <span>
                                <WarningOutlined style={{ color: '#d46b08', marginRight: 4 }} />
                                Unlinked Villages
                                <Badge count={unlinked.length} style={{ marginLeft: 8, background: '#d46b08' }} overflowCount={9999} />
                            </span>
                        ),
                        children: (
                            <>
                                {filterBar}
                                <Card size="small" title={
                                    <Text style={{ fontSize: 13 }}>
                                        {selectedKeys.length > 0
                                            ? `${selectedKeys.length} selected — map master then click "Apply Selected"`
                                            : 'Select rows → map master village → Apply Selected, or apply row by row'}
                                    </Text>
                                }>
                                    <Table {...tableProps(unlinked, unlinkedLoading, false)} />
                                </Card>
                            </>
                        ),
                    },
                    {
                        key: 'linked',
                        label: (
                            <span>
                                <EditOutlined style={{ color: '#fa8c16', marginRight: 4 }} />
                                Re-correct Linked
                                <Badge count={linked.length} style={{ marginLeft: 8, background: '#fa8c16' }} overflowCount={9999} />
                            </span>
                        ),
                        children: (
                            <>
                                <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
                                    <WarningOutlined style={{ color: '#d46b08', marginRight: 6 }} />
                                    These villages are already linked to a master but may be linked to the <b>wrong or incomplete</b> master.
                                    Orange tag = incomplete master (missing tehsil/district). Select new master and click <b>Re-apply</b> to overwrite.
                                </div>
                                {filterBar}
                                <Card size="small" title={
                                    <Text style={{ fontSize: 13 }}>
                                        {selectedKeys.length > 0
                                            ? `${selectedKeys.length} selected — select new master then click "Re-apply Selected"`
                                            : 'Select rows → map correct master → Re-apply to overwrite existing link'}
                                    </Text>
                                }>
                                    <Table {...tableProps(linked, linkedLoading, true)} />
                                </Card>
                            </>
                        ),
                    },
                ]}
            />
        </div>
    );
}
